import { deepCloneSafe } from '@src/utility/deepCloneSafe';
import { LogManager } from '@src/utility/Logger';
import {
  VIDEO_RENDER_FINISHED_EVENT,
  type VideoRenderFinishedEventDetail,
} from '@src/utility/videoRenderEvents';
import { useEffect } from 'react';

import type { ClipEditSessionStore } from '@src/stores-v2/ClipEditSessionStore';

const logger = LogManager.getLogger('useRenderStatusObserver');

/**
 * Bridges render-status updates from `VideoCreatorStore` into the modal's
 * session store.
 *
 * `applyFinishedVideoStatus` dispatches a `VIDEO_RENDER_FINISHED_EVENT`
 * once a render finishes. We listen for it and patch the
 * matching session-store variant by `id`. Using an event (rather than a MobX
 * reaction over `videoClipPreview`) means variants created during this
 * session — which live only in the session store and never reach the object
 * trees `applyFinishedVideoStatus` mutates — still get their publish
 * status, so the dimensions dropdown badge flips to "Final" live without
 * reopening the modal.
 */
export function useRenderStatusObserver(
  sessionStore: ClipEditSessionStore,
): void {
  useEffect(() => {
    const handler = (e: Event) => {
      const update = (e as CustomEvent<VideoRenderFinishedEventDetail>).detail;
      if (!update?.id) return;

      // Match against a session variant by id. Variants without an id are
      // unsaved (new AR creation in progress) and can't have render status.
      const variant = sessionStore.findVariantById(update.id);
      if (!variant) return;

      // deepCloneSafe strips any MobX proxy wrappers off the payload.
      sessionStore.updateVariant(
        variant.aspectRatio,
        deepCloneSafe({
          videoFilePrimary: update.videoFilePrimary,
          isHidden: update.isHidden,
          lastActionJson: update.lastActionJson,
          lastPublishJson: update.lastPublishJson,
        }),
      );

      logger.info('Patched render status into session', {
        id: update.id,
        ar: variant.aspectRatio,
      });
    };

    window.addEventListener(VIDEO_RENDER_FINISHED_EVENT, handler);
    return () =>
      window.removeEventListener(VIDEO_RENDER_FINISHED_EVENT, handler);
  }, [sessionStore]);
}
